<template>
  <div class="products-showcase-slider relative">
    <template v-if="withArrows">
      <button
        v-show="canScrollLeft"
        type="button"
        class="products-showcase-slider__arrow products-showcase-slider__arrow--left hidden md:flex"
        :aria-label="t('general.pagination.previous')"
        data-test-id="products-showcase-slider-arrow-left"
        @click="scrollByDirection('left')"
      >
        <span
          class="products-showcase-slider__arrow-button flex items-center justify-center"
        >
          <ChevronLeft class="mr-0.5 text-black" />
        </span>
      </button>

      <button
        v-show="canScrollRight"
        type="button"
        class="products-showcase-slider__arrow products-showcase-slider__arrow--right hidden md:flex"
        :aria-label="t('general.pagination.next')"
        data-test-id="products-showcase-slider-arrow-right"
        @click="scrollByDirection('right')"
      >
        <span
          class="products-showcase-slider__arrow-button flex items-center justify-center"
        >
          <ChevronRight class="ml-0.5 text-black" />
        </span>
      </button>
    </template>

    <div
      ref="scrollContainerRef"
      v-dragscroll.x="enableDragScroll"
      v-bind="$attrs"
      @scroll.passive="updateScrollState"
      @dragscrollstart="onDragStart"
      @click.capture="onDragClick"
    >
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { dragscroll } from 'vue-dragscroll'
import ChevronRight from '~/assets/icons/chevron-right.svg?component'
import ChevronLeft from '~/assets/icons/chevron-left.svg?component'

defineOptions({ inheritAttrs: false })

const vDragscroll = dragscroll

const DESKTOP_VIEWPORT = '(min-width: 768px)'
const SCROLL_EDGE_THRESHOLD = 50
/** Long enough for a smooth arrow scroll to finish before the loop re-centres. */
const SCROLL_SETTLE_MS = 400

/**
 * `withArrows` also gates drag-to-scroll: both only make sense when the
 * container keeps scrolling horizontally on desktop. When it collapses into a
 * grid at `md:` the arrows would point at nothing.
 */
const {
  withArrows = false,
  loopCopies = 1,
  stepByItem = false,
} = defineProps<{
  withArrows?: boolean
  /** Arrows advance one card instead of a full viewport-width page. */
  stepByItem?: boolean
  /**
   * How many identical copies of the slot the consumer renders. Anything above
   * 1 turns the rail into a loop: scrollLeft is shifted by a whole copy once the
   * reader drifts into an outer one, so the track never reaches an end and both
   * arrows stay live. The consumer owns the duplication because it owns the
   * item keys — rendering `<slot />` repeatedly here would collide them.
   */
  loopCopies?: number
}>()

const isLooping = computed(() => withArrows && loopCopies > 1)

const { t } = useI18n()

const scrollContainerRef = ref<HTMLElement | null>(null)
const enableDragScroll = ref(false)
const canScrollLeft = ref(false)
const canScrollRight = ref(false)
const dragged = ref(false)
const dragTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
const recentreTimer = ref<ReturnType<typeof setTimeout> | null>(null)

/** Distance from one card's left edge to the next, i.e. card width plus gap. */
const itemStride = () => {
  const items = scrollContainerRef.value?.children
  if (!items || items.length < 2) return 0

  return (
    (items[1] as HTMLElement).offsetLeft - (items[0] as HTMLElement).offsetLeft
  )
}

/**
 * Width of one copy of the slot, i.e. one full lap of the loop.
 *
 * Measured from the cards, not as `scrollWidth / loopCopies`: every copy sits in
 * the same flex container, so the track holds `items * copies - 1` gaps rather
 * than one per card. Dividing the total leaves each copy short by a fraction of
 * a gap, and the loop then shifts by that slightly-wrong amount — which reads as
 * a few pixels of drift once the scroll settles.
 */
const copyWidth = () => {
  const items = scrollContainerRef.value?.children
  if (!items || loopCopies < 2) return 0

  const perCopy = Math.floor(items.length / loopCopies)
  const first = items[0] as HTMLElement | undefined
  const nextCopy = items[perCopy] as HTMLElement | undefined
  if (!first || !nextCopy) return 0

  return nextCopy.offsetLeft - first.offsetLeft
}

/**
 * Shift by exactly one copy once the reader drifts into an outer one. The
 * content a copy over is identical, so the jump is invisible. It waits for the
 * scroll to settle because assigning scrollLeft mid-animation cancels the
 * smooth arrow scroll.
 */
const recentreLoop = () => {
  const container = scrollContainerRef.value
  const width = copyWidth()
  if (!container || !width) return

  if (container.scrollLeft < width * 0.5) container.scrollLeft += width
  else if (container.scrollLeft > width * 1.5) container.scrollLeft -= width
}

const scheduleRecentre = () => {
  if (recentreTimer.value) clearTimeout(recentreTimer.value)
  recentreTimer.value = setTimeout(recentreLoop, SCROLL_SETTLE_MS)
}

const updateScrollState = () => {
  const container = scrollContainerRef.value
  if (!container || !withArrows) return

  // A loop has no ends, so neither arrow is ever disabled.
  if (isLooping.value) {
    canScrollLeft.value = true
    canScrollRight.value = true
    scheduleRecentre()
    return
  }

  canScrollLeft.value = container.scrollLeft > SCROLL_EDGE_THRESHOLD
  canScrollRight.value =
    container.scrollLeft + container.offsetWidth <
    container.scrollWidth - SCROLL_EDGE_THRESHOLD
}

const scrollByDirection = (direction: 'left' | 'right') => {
  const container = scrollContainerRef.value
  if (!container) return

  const step = (stepByItem && itemStride()) || container.offsetWidth
  const offset = direction === 'right' ? step : -step

  container.scrollTo({
    left: container.scrollLeft + offset,
    behavior: 'smooth',
  })
}

const onDragStart = () => {
  if (!enableDragScroll.value) return

  if (dragTimeout.value) {
    clearTimeout(dragTimeout.value)
  }

  dragged.value = false
  dragTimeout.value = setTimeout(() => {
    dragged.value = true
  }, 100)
}

// Swallow the click that ends a drag so dragging the rail never opens a card.
const onDragClick = (event: Event) => {
  if (!enableDragScroll.value || !dragged.value) return

  event.preventDefault()
  dragged.value = false
}

onMounted(() => {
  if (!withArrows) return

  const mediaQuery = window.matchMedia(DESKTOP_VIEWPORT)
  const syncDragScroll = () => {
    enableDragScroll.value = mediaQuery.matches
    void nextTick(() => updateScrollState())
  }

  syncDragScroll()
  mediaQuery.addEventListener('change', syncDragScroll)

  if (isLooping.value) {
    void nextTick(() => {
      const container = scrollContainerRef.value
      if (container) container.scrollLeft = copyWidth()
    })
  }

  // Cards arrive asynchronously (Algolia) — recompute once the rail resizes.
  const resizeObserver = new ResizeObserver(() => updateScrollState())
  if (scrollContainerRef.value) {
    resizeObserver.observe(scrollContainerRef.value)
  }

  onBeforeUnmount(() => {
    mediaQuery.removeEventListener('change', syncDragScroll)
    resizeObserver.disconnect()
    if (dragTimeout.value) clearTimeout(dragTimeout.value)
    if (recentreTimer.value) clearTimeout(recentreTimer.value)
  })
})
</script>

<style lang="css" scoped>
@reference '~/assets/css/tailwind.css';
/* The three custom properties let a consumer re-anchor and de-fade the arrows
   without reaching in with :deep(). Fallbacks are the original hardcoded values,
   so callers that set nothing are unaffected.
   --slider-arrow-height: how far down the chevron centres (it is top-aligned and
   vertically centred within its own height), e.g. set it to the card image height
   to keep the arrows level with the images regardless of text length.
   --slider-arrow-inset: negative values push the arrows out of the track, into a
   gutter, instead of overlaying the first/last card.
   --slider-arrow-fade: the colour the gradient fades from; set it to transparent
   on non-white backgrounds. */
.products-showcase-slider__arrow {
  position: absolute;
  top: 0;
  z-index: 3;
  height: var(--slider-arrow-height, 69%);
  width: 50px;
  align-items: center;
  border: 0;
  padding: 0;
  cursor: pointer;
}

.products-showcase-slider__arrow--left {
  left: var(--slider-arrow-inset, 0);
  justify-content: flex-start;
  background: linear-gradient(
    to right,
    var(--slider-arrow-fade, var(--color-white)),
    rgba(0, 0, 0, 0)
  );
}

.products-showcase-slider__arrow--right {
  right: var(--slider-arrow-inset, 0);
  justify-content: flex-end;
  background: linear-gradient(
    to right,
    rgba(0, 0, 0, 0),
    var(--slider-arrow-fade, var(--color-white))
  );
}

.products-showcase-slider__arrow-button {
  @apply w-10 h-10 rounded-full border border-gray-200 bg-white;
}
</style>
